import { type ProductLine, ProductLineFields } from "@cleanbudget/contract/product-line";
import { Alert, AlertDescription } from "@cleanbudget/ui/components/alert";
import { Button } from "@cleanbudget/ui/components/button";
import { Card, CardContent, CardHeader, CardTitle } from "@cleanbudget/ui/components/card";
import { IconAlertCircle, IconPencil, IconTrash } from "@tabler/icons-react";
import { useMutation, useQueryClient, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { match } from "ts-pattern";
import { BackLink } from "@/components/back-link";
import { ConfirmDialog } from "@/components/confirm-dialog";
import { type Column, DataTable } from "@/components/data-table/data-table";
import { Form, FormError, SubmitButton, TextField, useZodForm } from "@/components/form/form";
import { scrollToForm } from "@/components/order-buttons";
import { PageHeader } from "@/components/page-header";
import { apiErrorTag } from "@/lib/api-error";
import { orpc } from "@/lib/orpc";

const linesQuery = () => orpc.productLine.list.queryOptions();

/**
 * Producten → Lijnen (#254): the lines, each a series of packs of one refill, with the products in
 * them. Which product is in which line is chosen in the product editor; a line with products in it
 * cannot be deleted, so its delete button stays off and says why.
 */
export const Route = createFileRoute("/_admin/product/line/")({
	loader: ({ context }) => context.queryClient.ensureQueryData(linesQuery()),
	component: ProductLinesPage,
});

/** Every write answers the whole list: it replaces the cached one, and product editors are stale. */
function useShowLines() {
	const queryClient = useQueryClient();
	return (list: ProductLine[]) => {
		queryClient.setQueryData(linesQuery().queryKey, list);
		void queryClient.invalidateQueries({ queryKey: orpc.product.get.key() });
	};
}

function ProductLinesPage() {
	const { t } = useTranslation("productLine");
	const { t: tProduct } = useTranslation("product");
	const { t: tCommon } = useTranslation();
	const queryClient = useQueryClient();
	const { data: lines } = useSuspenseQuery(linesQuery());
	const show = useShowLines();
	const [editingId, setEditingId] = useState<string>();
	const [problem, setProblem] = useState<string>();
	const editing = lines.find((line) => line.id === editingId);

	const remove = useMutation({ ...orpc.productLine.delete.mutationOptions(), onSuccess: show });

	const onDelete = async (line: ProductLine) => {
		setProblem(undefined);
		try {
			await remove.mutateAsync({ productLineId: line.id });
			if (editingId === line.id) setEditingId(undefined);
		} catch (error) {
			setProblem(
				match(apiErrorTag(error))
					.with("ProductLineNotEmpty", () => t("error.notEmpty"))
					.with("ProductLineNotFound", () => t("error.notFound"))
					.otherwise(() => tCommon("form.failed")),
			);
			await queryClient.invalidateQueries({ queryKey: linesQuery().queryKey });
		}
	};

	const columns: readonly Column<ProductLine>[] = [
		{
			id: "name",
			header: t("column.name"),
			cell: (line) => (
				<span className="flex flex-col gap-0.5">
					<span className="font-medium whitespace-normal">{line.name}</span>
					<span className="text-xs text-muted-foreground md:hidden">
						{t("productCount", { count: line.products.length })}
					</span>
				</span>
			),
		},
		{
			id: "products",
			header: t("column.products"),
			secondary: true,
			cell: (line) =>
				line.products.length === 0 ? (
					<span className="text-muted-foreground">{t("noProducts")}</span>
				) : (
					<ul className="flex flex-col gap-0.5 whitespace-normal">
						{line.products.map((product) => (
							<li key={product.id}>
								<Link
									to="/product/$productId"
									params={{ productId: product.id }}
									className="underline-offset-4 hover:underline"
								>
									{product.name}
								</Link>
							</li>
						))}
					</ul>
				),
		},
		{
			id: "actions",
			header: <span className="sr-only">{t("column.actions")}</span>,
			align: "right",
			cell: (line) => (
				<span className="flex justify-end gap-1">
					<Button
						variant="ghost"
						size="icon-sm"
						aria-label={t("edit.button", { name: line.name })}
						onClick={() => {
							setEditingId(line.id);
							scrollToForm("product-line-form");
						}}
					>
						<IconPencil aria-hidden />
					</Button>
					{line.products.length > 0 ? (
						<Button
							variant="ghost"
							size="icon-sm"
							disabled
							aria-label={t("delete.button", { name: line.name })}
							title={t("delete.notEmpty")}
						>
							<IconTrash aria-hidden />
						</Button>
					) : (
						<ConfirmDialog
							trigger={
								<Button variant="ghost" size="icon-sm" aria-label={t("delete.button", { name: line.name })}>
									<IconTrash aria-hidden />
								</Button>
							}
							title={t("delete.title", { name: line.name })}
							description={t("delete.description")}
							confirmLabel={t("delete.confirm")}
							onConfirm={() => onDelete(line)}
						/>
					)}
				</span>
			),
		},
	];

	return (
		<div className="flex max-w-3xl flex-col gap-6">
			<PageHeader
				back={<BackLink to="/product">{tProduct("title")}</BackLink>}
				title={t("title")}
				description={t("description")}
			/>
			{problem ? (
				<Alert variant="destructive">
					<IconAlertCircle aria-hidden />
					<AlertDescription>{problem}</AlertDescription>
				</Alert>
			) : null}
			<div className="flex flex-col gap-3">
				{lines.some((line) => line.products.length > 0) ? (
					<p className="text-sm text-muted-foreground">{t("delete.notEmpty")}</p>
				) : null}
				<DataTable
					label={t("list")}
					columns={columns}
					rows={lines}
					rowKey={(line) => line.id}
					empty={<p className="p-6 text-center text-sm text-muted-foreground">{t("empty")}</p>}
				/>
			</div>
			<div id="product-line-form" className="scroll-mt-4">
				{editing ? (
					<ProductLineForm key={editing.id} line={editing} onDone={() => setEditingId(undefined)} />
				) : (
					<ProductLineForm />
				)}
			</div>
		</div>
	);
}

/** A new line, or (with `line`) its new name. */
function ProductLineForm({ line, onDone }: { line?: ProductLine; onDone?: () => void }) {
	const { t } = useTranslation("productLine");
	const { t: tCommon } = useTranslation();
	const show = useShowLines();
	const form = useZodForm(ProductLineFields, { defaultValues: { name: line?.name ?? "" } });
	const create = useMutation({ ...orpc.productLine.create.mutationOptions(), onSuccess: show });
	const update = useMutation({ ...orpc.productLine.update.mutationOptions(), onSuccess: show });

	const onSubmit = async (values: ProductLineFields) => {
		try {
			if (line) await update.mutateAsync({ productLineId: line.id, ...values });
			else await create.mutateAsync(values);
			form.reset({ name: "" });
			onDone?.();
		} catch (error) {
			match(apiErrorTag(error))
				.with("ProductLineNameTaken", () =>
					form.setError("name", { type: "server", message: t("error.nameTaken") }),
				)
				.with("ProductLineNotFound", () =>
					form.setError("root", { type: "server", message: t("error.notFound") }),
				)
				.otherwise(() => form.setError("root", { type: "server", message: tCommon("form.failed") }));
		}
	};

	return (
		<Card>
			<CardHeader>
				<CardTitle>{line ? t("edit.title", { name: line.name }) : t("new.title")}</CardTitle>
			</CardHeader>
			<CardContent>
				<Form form={form} onSubmit={onSubmit}>
					<TextField
						control={form.control}
						name="name"
						label={t("field.name")}
						description={t("field.nameDescription")}
						placeholder={t("field.namePlaceholder")}
						autoComplete="off"
					/>
					<FormError control={form.control} />
					<div className="flex flex-wrap gap-2">
						<SubmitButton control={form.control}>{line ? t("edit.submit") : t("new.submit")}</SubmitButton>
						{line ? (
							<Button type="button" variant="outline" onClick={onDone}>
								{t("edit.cancel")}
							</Button>
						) : null}
					</div>
				</Form>
			</CardContent>
		</Card>
	);
}
